import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type CourseDetail, type CourseLesson } from '../api.ts';
import { Progress } from '../components/Progress.tsx';

function LessonRow({ lesson }: { lesson: CourseLesson }): React.JSX.Element {
  const done = lesson.state === 'completed';
  // Handed in, waiting for the teacher — open onward, not yet counted.
  const waiting = lesson.state === 'submitted';
  const started = lesson.state === 'in_progress';

  const markClass = done
    ? ' lesson__state--done'
    : waiting
      ? ' lesson__state--waiting'
      : started
        ? ' lesson__state--current'
        : '';

  const meta = done
    ? 'Пройден'
    : waiting
      ? 'На проверке'
      : !lesson.available
      ? (lesson.locked_reasons[0] ?? 'Закрыт')
      : lesson.estimated_minutes
        ? `${lesson.estimated_minutes} мин`
        : '';

  const body = (
    <>
      <span className={`lesson__state${markClass}`} aria-hidden="true">
        {done ? '✓' : waiting ? '✓' : started ? '•' : ''}
      </span>
      <span>{lesson.title}</span>
      <span className="lesson__meta">{meta}</span>
    </>
  );

  // A locked lesson is not a link at all: nothing to click, nothing to guess at.
  return lesson.available ? (
    <Link to={`/lessons/${lesson.id}`} className="lesson lesson--open">
      {body}
    </Link>
  ) : (
    <div className="lesson lesson--locked">{body}</div>
  );
}

/**
 * The course as a sequence.
 *
 * Locked material stays visible so the learner can see the road ahead, but it carries the reason
 * the server computed — never a bare padlock. "Finish the previous lesson" is an instruction;
 * a padlock on its own is a support ticket.
 */
export function CoursePage(): React.JSX.Element {
  const { courseId } = useParams();
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!courseId) return;
    api
      .course(courseId)
      .then(setCourse)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить курс'),
      );
  }, [courseId]);

  if (error) {
    return (
      <p className="error" style={{ paddingTop: 40 }}>
        {error}
      </p>
    );
  }

  if (!course) {
    return (
      <p className="muted" style={{ paddingTop: 40 }}>
        Загружаем…
      </p>
    );
  }

  const next = course.modules
    .filter((m) => m.available)
    .flatMap((m) => m.lessons)
    // The first lesson not yet handed in: a lesson waiting for the teacher is not where to resume.
    .find((l) => l.available && l.state !== 'completed' && l.state !== 'submitted');

  return (
    <>
      <div className="page-head">
        <Link to="/" className="back">
          ← Мои курсы
        </Link>
        <h1>{course.title}</h1>
        {course.summary ? (
          <p className="muted" style={{ maxWidth: '60ch' }}>
            {course.summary}
          </p>
        ) : null}

        <div className="row" style={{ marginTop: 22 }}>
          {/* The server keeps two decimals; a learner wants a number, not a measurement. */}
          <div style={{ minWidth: 260, flex: 1 }}>
            <Progress percent={course.progress_percent} />
          </div>

          {next ? (
            <Link to={`/lessons/${next.id}`} className="btn">
              Продолжить: {next.title}
            </Link>
          ) : null}
        </div>
      </div>

      {course.modules.map((courseModule, index) => (
        <section
          key={courseModule.id}
          className={`module${courseModule.available ? '' : ' module--locked'}`}
        >
          <header className="module__head">
            <span className="module__index">{String(index + 1).padStart(2, '0')}</span>
            <span className="module__title">{courseModule.title}</span>
            {courseModule.completed ? (
              <span className="module__reason" style={{ color: 'var(--done)' }}>
                Пройден
              </span>
            ) : !courseModule.available ? (
              <span className="module__reason">
                🔒 {courseModule.locked_reasons[0] ?? 'Закрыт'}
              </span>
            ) : null}
          </header>

          {courseModule.lessons.map((lesson) => (
            <LessonRow key={lesson.id} lesson={lesson} />
          ))}
        </section>
      ))}
    </>
  );
}
